import React, { useState } from 'react';
import { Key, Shield, RefreshCw, Download, Upload, Lock, AlertTriangle } from 'lucide-react';

export const UserEncryptionManager: React.FC = () => {
  const [encryptionStatus] = useState({
    keyId: 'key-patient-1',
    algorithm: 'AES-256-GCM',
    status: 'active',
    createdAt: '2024-01-01T00:00:00Z',
    lastRotated: '2024-01-01T00:00:00Z',
    rotationDue: false,
    dataEncrypted: true,
  });

  const [showBackupKey, setShowBackupKey] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-slate-800">Encryption Management</h3>
          <p className="text-slate-600 mt-1">Manage your personal encryption keys and data security</p>
        </div>
        <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg">
          <Shield className="h-4 w-4" />
          <span className="text-sm font-medium">Fully Encrypted</span>
        </div>
      </div>

      {/* Encryption Status */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h4 className="text-lg font-semibold text-slate-800 mb-4">Your Encryption Status</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Encryption Algorithm</label>
              <p className="text-lg font-mono text-slate-800">{encryptionStatus.algorithm}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Key Status</label>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-slate-800 capitalize">{encryptionStatus.status}</span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Last Key Rotation</label>
              <p className="text-slate-800">{new Date(encryptionStatus.lastRotated).toLocaleDateString()}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Key ID</label>
              <code className="block text-sm bg-slate-100 px-3 py-2 rounded">
                {encryptionStatus.keyId}
              </code>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Data Encryption</label>
              <div className="flex items-center space-x-2">
                <Lock className="h-4 w-4 text-green-500" />
                <span className="text-green-700">All data encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Management Actions */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h4 className="text-lg font-semibold text-slate-800 mb-4">Key Management</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => setShowBackupKey(!showBackupKey)}
            className="flex items-center space-x-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Download className="h-6 w-6 text-blue-500" />
            <div className="text-left">
              <p className="font-medium text-slate-800">Backup Key</p>
              <p className="text-sm text-slate-600">Export encrypted backup</p>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
            <RefreshCw className="h-6 w-6 text-green-500" />
            <div className="text-left">
              <p className="font-medium text-slate-800">Rotate Key</p>
              <p className="text-sm text-slate-600">Generate new encryption key</p>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
            <Upload className="h-6 w-6 text-purple-500" />
            <div className="text-left">
              <p className="font-medium text-slate-800">Import Key</p>
              <p className="text-sm text-slate-600">Restore from backup</p>
            </div>
          </button>
        </div>
      </div>

      {/* Backup Key Display */}
      {showBackupKey && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start space-x-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mt-1" />
            <div>
              <h4 className="font-semibold text-yellow-800">Encryption Key Backup</h4>
              <p className="text-sm text-yellow-700">
                Store this backup key securely. You'll need it to recover your data if you lose access to your account.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-yellow-300">
            <label className="block text-sm font-medium text-slate-700 mb-2">Encrypted Backup Key</label>
            <textarea 
              className="w-full h-24 p-3 border border-slate-300 rounded-lg font-mono text-sm"
              readOnly
              value="-----BEGIN ENCRYPTED PRIVATE KEY-----
MIIEvgIBADANBgkqhkiG9w0BAQEFAASCBKgwggSkAgEAAoIBAQC7VJTUt9Us8cKB
wko6piHNzN2OQ7B7R6Q5mJ+w4jP3m2Q6FJ2KRvdgkUx7K3GZqQ8F5qQ9Q3K5g8J
...(truncated for security)...
-----END ENCRYPTED PRIVATE KEY-----"
            />
            <div className="flex justify-between items-center mt-3">
              <p className="text-xs text-slate-500">Keep this backup in a secure location</p>
              <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Recommendations */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h4 className="text-lg font-semibold text-slate-800 mb-4">Security Recommendations</h4>
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <Shield className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-800">Regular key rotation is enabled</span>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <Lock className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-800">All data is encrypted with your personal key</span>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Key className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-blue-800">Consider backing up your encryption key</span>
          </div>
        </div>
      </div>
    </div>
  );
};
